import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { readMyUser } from '../services/myUser';
import { useCreatorsEarned } from '../services/creatorsEarned';
import {
  UserCircleIcon,
  VideoCameraIcon,
  CurrencyDollarIcon,
  CheckIcon,
  ArrowRightIcon,
  DevicePhoneMobileIcon,
  SparklesIcon,
  GlobeAltIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { StarIcon, CheckBadgeIcon, XMarkIcon } from '@heroicons/react/24/solid';
import ImageUpload from '../components/ImageUpload';
import Logo from '../components/Logo';
import PricingHelper from '../components/onboarding/PricingHelper';
import { TalentCategory } from '../types';
import { uploadVideoToWasabi } from '../services/videoUpload';
import { TARGETING_CATEGORIES } from '../constants/targetingCategories';
import toast from 'react-hot-toast';
import { formatPhoneInput, otpErrorMessage, PHONE_FORMAT_ERROR, PHONE_HELP_TEXT, toE164 } from '../utils/phone';
import { isUsernameAvailable, isUsernameTakenError } from '../utils/usernameAvailability';
import { startProfileFieldsError, startProfileUpdate } from '../utils/startProfile';
import {
  acceptCreatorTermsAtSignup,
  getCreatorTermsSignupVersion,
  isTermsRefusal,
  markSignupTermsPending,
} from '../design/data/creatorTerms';
import CreatorTermsCheckbox from '../components/CreatorTermsCheckbox';
import {
  checkSignupDuplicates,
  recordSignupDuplicate,
  SignupDuplicateCheck,
  SignupDuplicatePage,
} from '../services/duplicatePages';
import {
  normalizeClaimSlug,
  pageClaimErrorMessage,
  PAGE_CLAIM_STORAGE_KEY,
  startPageClaim,
} from '../utils/pageClaim';
import { claimFailureCategory, claimFunnelContext, trackClaimStep } from '../utils/claimFunnel';

const STEPS = [
  { id: 1, name: 'Create Account', description: 'Sign up or login', icon: UserCircleIcon },
  { id: 2, name: 'Profile & Categories', description: 'Your public profile', icon: StarIcon },
  { id: 3, name: 'Promo Video', description: 'Introduce yourself', icon: VideoCameraIcon },
  { id: 4, name: 'Your Bio Link', description: 'Customize your link', icon: GlobeAltIcon },
];

const CATEGORY_OPTIONS: { value: TalentCategory; label: string }[] = [
  { value: 'politician', label: 'Politician' },
  { value: 'candidate', label: 'Candidate' },
  { value: 'party-leader', label: 'Party Leader' },
  { value: 'reporter', label: 'Reporter' },
  { value: 'tv-host', label: 'TV Host' },
  { value: 'commentator', label: 'Commentator' },
  { value: 'author', label: 'Author' },
  { value: 'comedian', label: 'Comedian' },
  { value: 'musician', label: 'Musician' },
  { value: 'actor', label: 'Actor' },
  { value: 'influencer', label: 'Influencer' },
  { value: 'activist', label: 'Activist' },
  { value: 'faith-leader', label: 'Faith Leader' },
  { value: 'academic', label: 'Academic' },
  { value: 'military', label: 'Military' },
  { value: 'youth-leader', label: 'Youth Leader' },
  { value: 'patriotic-entertainer', label: 'Patriotic Entertainer' },
  { value: 'other', label: 'Other' },
];

const bioCreatorBios = [
  { name: 'Chris Ripa', handle: 'chrisripa' },
  { name: 'Greg On Fire', handle: 'gregonfire' },
  { name: 'Shawn Farash', handle: 'shawnfarash' },
  { name: 'Melonie Mac', handle: 'meloniemac' },
  { name: 'Nick Di Paolo', handle: 'nickdipaolo' },
  { name: 'Hayley Caronia', handle: 'hayleycaronia' },
  { name: 'Lydia Shaffer', handle: 'lydiashaffer' },
  { name: 'Kristin Sokoloff', handle: 'kristinsokoloff' },
];

// "Not mine" answers to the duplicate-page prompt not saved yet, one per page
// (kept after safeStorage below), with the name and username it was about.
const PENDING_DUPLICATE_ANSWERS = 'pending_duplicate_answers';
type PendingDuplicateAnswer = { talentId: string; fullName: string; username: string };

// Safari Private mode blocks localStorage writes and throws QuotaExceededError.
// This helper falls back to sessionStorage silently so the flow never crashes.
const safeStorage = {
  getItem: (key: string): string | null => {
    try { return localStorage.getItem(key); } catch {
      try { return sessionStorage.getItem(key); } catch { return null; }
    }
  },
  setItem: (key: string, value: string): void => {
    try { localStorage.setItem(key, value); } catch {
      try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
    }
  },
  removeItem: (key: string): void => {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
    try { sessionStorage.removeItem(key); } catch { /* ignore */ }
  },
};

function readPendingDuplicateAnswers(): PendingDuplicateAnswer[] {
  try {
    const list = JSON.parse(safeStorage.getItem(PENDING_DUPLICATE_ANSWERS) || '[]');
    return Array.isArray(list)
      ? list.filter(
          (p) => p && typeof p.talentId === 'string' && typeof p.fullName === 'string' && typeof p.username === 'string'
        )
      : [];
  } catch {
    return [];
  }
}

/**
 * Keeps (keep = true) a page's answer, replacing any older one for that page,
 * or removes it once saved, unless a newer answer for the page has replaced it.
 * Other pages' answers are left alone.
 */
function updatePendingDuplicateAnswer(answer: PendingDuplicateAnswer, keep: boolean) {
  const current = readPendingDuplicateAnswers();
  const mine = current.find((p) => p.talentId === answer.talentId);
  if (!keep && mine && (mine.fullName !== answer.fullName || mine.username !== answer.username)) return;
  const others = current.filter((p) => p.talentId !== answer.talentId);
  const list = keep ? [...others, answer] : others;
  if (list.length) safeStorage.setItem(PENDING_DUPLICATE_ANSWERS, JSON.stringify(list));
  else safeStorage.removeItem(PENDING_DUPLICATE_ANSWERS);
}

const StartPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Tolerate malformed URLs like "&?ref=foo" where the extra "?" makes the
  // query key literally "?ref" instead of "ref".
  const partnerRef = (searchParams.get('ref') || searchParams.get('?ref') || '').trim();
  const adminToken = (searchParams.get('token') || searchParams.get('?token') || '').trim();
  // Acquisition source: ?utm=ad (the in-app "Creators" house ad). Captured at
  // signup and stored on the profile so admin > shoutout fans can flag these
  // creators the same way partner referrals are flagged.
  const utmSource = (searchParams.get('utm') || searchParams.get('utm_source') || searchParams.get('?utm') || '').trim().toLowerCase();
  // Claim from the page (JT, 27 Oct 2026): an unclaimed shoutout.fans page's
  // "claim it free" bar opens /start?claim=<slug>. Kept through the OTP
  // round-trip; claimed once they're signed in (handleUserAuthenticated).
  const claimParam = normalizeClaimSlug(searchParams.get('claim'));
  const [pendingClaimSlug, setPendingClaimSlug] = useState<string | null>(
    () => claimParam || normalizeClaimSlug(safeStorage.getItem(PAGE_CLAIM_STORAGE_KEY))
  );
  // Straight to sign-up: they came to claim a page, not for the overview.
  const [currentStep, setCurrentStep] = useState(() => (claimParam ? 1 : 0));
  // The claim funnel (JT via Creator Brain, PROJECT 300): which page this
  // visit claims (its onboarding link, else the bar's slug), fixed for the tab
  // so the steps after the claim still count. Outside a claim, nothing is sent.
  const claimFunnel = useMemo(
    () =>
      claimFunnelContext(
        adminToken || safeStorage.getItem('admin_onboard_token'),
        claimParam || normalizeClaimSlug(safeStorage.getItem(PAGE_CLAIM_STORAGE_KEY))
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(() => {
    trackClaimStep(claimFunnel, 'claim_start_viewed');
  }, [claimFunnel]);
  useEffect(() => {
    if (currentStep >= 1 && currentStep <= 4) trackClaimStep(claimFunnel, 'claim_step_reached', { step: currentStep });
  }, [claimFunnel, currentStep]);
  const [skipPromoVideo, setSkipPromoVideo] = useState(!!partnerRef);
  // Onboarding v2 (creator_onboarding_v2 switch): no promo video step, and a
  // link-in-bio ask on the last step. null = not known yet.
  const [onboardingV2, setOnboardingV2] = useState<boolean | null>(null);
  const creatorsEarned = useCreatorsEarned();
  const [bioLinkCopied, setBioLinkCopied] = useState(false);
  // The last step's "I agree to the ShoutOut Creator Terms" box. Unticked to
  // start; finishing waits for it (13 Oct 2026). Always shown.
  const [termsAgreed, setTermsAgreed] = useState(false);
  // The Terms version that box stands for, read when the last step opens. It
  // only pins a retry if recording fails; null = not read (yet). A ref, so a
  // finish already under way sees a read that lands meanwhile.
  const termsVersionRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [referrer, setReferrer] = useState<{ name: string; avatar: string | null; username: string | null } | null>(null);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [defaultAdminFee, setDefaultAdminFee] = useState(25);

  // OTP Auth State (same flow as admin onboarding)
  const [authStep, setAuthStep] = useState<'email' | 'phone' | 'otp'>('email');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [existingUserPhone, setExistingUserPhone] = useState<string | null>(null);
  // True when the OTP was sent to a phone already on file. The server no longer
  // returns the raw phone (security), so verify/resend resolve it by email.
  const [phoneOnFile, setPhoneOnFile] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);
  const otpInputRef = useRef<HTMLInputElement>(null);

  const [userId, setUserId] = useState<string | null>(null);
  const [talentProfileId, setTalentProfileId] = useState<string | null>(null);

  // Intro carousel state
  const [featuredTalent, setFeaturedTalent] = useState<Array<{ name: string; avatar: string; handle: string }>>([]);
  const introCarouselRef = useRef<HTMLDivElement>(null);

  // FOMO signup ticker (bottom-right): most-recent creators, newest first
  type FomoSignup = { name: string; avatar: string; handle: string; createdAt: string };
  const [fomoSignups, setFomoSignups] = useState<FomoSignup[]>([]);
  const [fomoIndex, setFomoIndex] = useState(0);
  const [fomoVisible, setFomoVisible] = useState(false);
  const [fomoDismissed, setFomoDismissed] = useState(false);

  // Profile Data
  const [profileData, setProfileData] = useState({
    fullName: '',
    username: '',
    category: '' as TalentCategory,
    categories: [] as TalentCategory[],
    targetingCategory: '',
    pricing: 50,
    instagramFollowers: 0,
    fulfillmentTime: 72,
    avatarUrl: '',
  });
  const [selectedShoutoutTypes, setSelectedShoutoutTypes] = useState<string[]>([]);

  // Claim-your-account flow: typeahead over UNCLAIMED talent profiles
  // (user_id IS NULL — admin pre-created pages with no registered account).
  type ClaimProfile = { id: string; username: string | null; name: string; avatar_url: string | null; bio: string | null };
  const [claimResults, setClaimResults] = useState<ClaimProfile[]>([]);
  const [claimField, setClaimField] = useState<'name' | 'username' | null>(null);
  const [claimCandidate, setClaimCandidate] = useState<ClaimProfile | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimedProfileId, setClaimedProfileId] = useState<string | null>(null);

  // One person, one page (O19): before the profile is saved, unclaimed pages
  // with exactly the display name or username typed here. The creator can
  // claim one, ask for its claim link, or say it isn't theirs. It never blocks
  // signup; the answer goes on the staff review list.
  type DuplicateSearch = { fullName: string; username: string };
  const [duplicatePrompt, setDuplicatePrompt] = useState<(SignupDuplicateCheck & DuplicateSearch) | null>(null);
  // The page the creator already answered for (not asked again).
  const [duplicateAnsweredFor, setDuplicateAnsweredFor] = useState<string | null>(null);
  // The name and username searched, when the claim modal was opened from that prompt.
  const [claimFromPrompt, setClaimFromPrompt] = useState<DuplicateSearch | null>(null);
  // No claim link: 'ask' offers to request one, 'sent' confirms it.
  const [claimLinkStep, setClaimLinkStep] = useState<'ask' | 'sending' | 'sent' | null>(null);
  // The page opened with a working onboarding link (built for them by staff).
  const [linkedPageId, setLinkedPageId] = useState<string | null>(null);

  // Multi-select dropdown open states
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [occasionDropdownOpen, setOccasionDropdownOpen] = useState(false);
  const [contentTypeDropdownOpen, setContentTypeDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const occasionDropdownRef = useRef<HTMLDivElement>(null);
  const contentTypeDropdownRef = useRef<HTMLDivElement>(null);

  // Bio link carousel
  const bioLinkCarouselRef = useRef<HTMLDivElement>(null);

  // Video
  const [promoVideo, setPromoVideo] = useState<File | null>(null);
  const [videoUploading, setVideoUploading] = useState(false);

  useEffect(() => {
    fetchPlatformSettings();
    loadSavedProgress();
    // Persist the acquisition source so it survives the OTP round-trip /
    // page reloads before the profile row is created.
    if (utmSource) safeStorage.setItem('signup_utm', utmSource);
    if (claimParam) safeStorage.setItem(PAGE_CLAIM_STORAGE_KEY, claimParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // OTP Cooldown timer
  useEffect(() => {
    if (otpCooldown > 0) {
      const timer = setTimeout(() => setOtpCooldown(otpCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCooldown]);

  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user && currentStep <= 1) {
        handleUserAuthenticated(session.user.id);
      }
    };
    checkExistingSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch featured talent for intro carousel
  useEffect(() => {
    const fetchFeatured = async () => {
      // Hand-picked creators always shown first in the carousel
      const pinnedUsernames = ['gregonfire', 'princessballoveras', 'barrrnone', 'notmelissavitelli'];

      const [{ data: pinned }, { data }] = await Promise.all([
        supabase
          .from('talent_profiles')
          .select('full_name, username, temp_avatar_url')
          .in('username', pinnedUsernames)
          .eq('is_active', true),
        supabase
          .from('talent_profiles')
          .select('full_name, username, temp_avatar_url')
          .eq('is_active', true)
          .not('temp_avatar_url', 'is', null)
          .neq('temp_avatar_url', '')
          .order('is_featured', { ascending: false })
          .order('total_orders', { ascending: false })
          .limit(16),
      ]);

      const toEntry = (t: { full_name: string | null; username: string | null; temp_avatar_url: string | null }) => ({
        name: t.full_name || t.username || '',
        avatar: t.temp_avatar_url || '',
        handle: t.username || '',
      });

      // Keep the hand-picked order, then fill with top talent (deduped)
      const pinnedSorted = (pinned || [])
        .sort((a, b) => pinnedUsernames.indexOf(a.username || '') - pinnedUsernames.indexOf(b.username || ''))
        .map(toEntry);
      const rest = (data || [])
        .map(toEntry)
        .filter((t) => !pinnedUsernames.includes(t.handle));

      setFeaturedTalent([...pinnedSorted, ...rest].filter((t) => t.avatar && t.name));
    };
    fetchFeatured();
  }, []);

  // Fetch most-recent creator signups for the FOMO ticker (newest first)
  useEffect(() => {
    const fetchSignups = async () => {
      const { data } = await supabase
        .from('talent_profiles')
        .select('full_name, username, temp_avatar_url, created_at')
        .eq('is_active', true)
        .not('temp_avatar_url', 'is', null)
        .neq('temp_avatar_url', '')
        .order('created_at', { ascending: false })
        .limit(30);

      if (data) {
        setFomoSignups(
          data
            .map((t) => ({
              name: t.full_name || t.username || '',
              avatar: t.temp_avatar_url || '',
              handle: t.username || '',
              createdAt: t.created_at as string,
            }))
            .filter((t) => t.avatar && t.name)
        );
      }
    };
    fetchSignups();
  }, []);

  // Cycle the FOMO ticker: show ~5s, hide ~0.5s, advance to next signup
  useEffect(() => {
    if (fomoDismissed || fomoSignups.length === 0) return;
    let advanceTimer: ReturnType<typeof setTimeout>;
    const showTimer = setTimeout(() => setFomoVisible(true), 2500);
    const hideTimer = setTimeout(() => {
      setFomoVisible(false);
      advanceTimer = setTimeout(() => setFomoIndex((i) => (i + 1) % fomoSignups.length), 500);
    }, 7500);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      clearTimeout(advanceTimer);
    };
  }, [fomoIndex, fomoSignups, fomoDismissed]);

  // Fetch referring partner's talent profile (avatar + name) for "Join {name} on ShoutOut." header
  useEffect(() => {
    const refToken = partnerRef || (typeof window !== 'undefined' ? safeStorage.getItem('partner_ref') : null);
    if (!refToken) return;

    const fetchReferrer = async () => {
      // One SECURITY DEFINER RPC that accepts either the partner's referral_token
      // or the partner's linked talent username. Callable by anon visitors so
      // logged-out new users see the referrer header immediately.
      const { data, error } = await supabase.rpc('get_partner_referrer_display', { p_token: refToken });
      if (error || !data) return;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return;
      setReferrer({
        name: row.name || '',
        avatar: row.avatar_url || null,
        username: row.username || null,
      });
    };
    fetchReferrer().catch(() => {});
  }, [partnerRef]);

  // Auto-scroll intro carousel
  useEffect(() => {
    if (currentStep !== 0) return;
    const carousel = introCarouselRef.current;
    if (!carousel || featuredTalent.length === 0) return;

    let animationId: number;
    let position = 0;
    const speed = 0.5;

    const animate = () => {
      position -= speed;
      const itemWidth = 88 + 16;
      if (Math.abs(position) >= itemWidth * featuredTalent.length) position = 0;
      carousel.style.transform = `translateX(${position}px)`;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [currentStep, featuredTalent]);

  // Auto-scroll bio link carousel (step 4)
  useEffect(() => {
    if (currentStep !== 4) return;
    const carousel = bioLinkCarouselRef.current;
    if (!carousel) return;

    let animationId: number;
    let position = 0;
    const speed = 0.4;

    const animate = () => {
      position -= speed;
      const cardWidth = 120 + 12;
      if (Math.abs(position) >= cardWidth * bioCreatorBios.length) position = 0;
      carousel.style.transform = `translateX(${position}px)`;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [currentStep]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target as Node)) {
        setCategoryDropdownOpen(false);
      }
      if (occasionDropdownRef.current && !occasionDropdownRef.current.contains(e.target as Node)) {
        setOccasionDropdownOpen(false);
      }
      if (contentTypeDropdownRef.current && !contentTypeDropdownRef.current.contains(e.target as Node)) {
        setContentTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPlatformSettings = async () => {
    try {
      const { data } = await supabase.from('app_settings').select('global_admin_fee_percentage').single();
      if (data?.global_admin_fee_percentage) setDefaultAdminFee(data.global_admin_fee_percentage);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const loadSavedProgress = () => {
    if (partnerRef) safeStorage.setItem('partner_ref', partnerRef);
    const saved = safeStorage.getItem('talent_start_progress');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.currentStep && parsed.currentStep > 1) setCurrentStep(parsed.currentStep);
        if (parsed.userId) setUserId(parsed.userId);
        if (parsed.talentProfileId) setTalentProfileId(parsed.talentProfileId);
        if (parsed.profileData) setProfileData({ ...profileData, ...parsed.profileData });
        if (parsed.selectedShoutoutTypes) setSelectedShoutoutTypes(parsed.selectedShoutoutTypes);
        if (parsed.currentStep > 1) toast.success('Progress restored!');
      } catch (e) {
        console.error('Failed to parse saved progress:', e);
      }
    }
  };

  // Save progress (localStorage + DB)
  useEffect(() => {
    if (userId || currentStep > 1) {
      safeStorage.setItem('talent_start_progress', JSON.stringify({
        currentStep, userId, talentProfileId, profileData, selectedShoutoutTypes,
      }));
    }
    if (talentProfileId && currentStep > 1) {
      supabase.from('talent_profiles').update({ current_onboarding_step: currentStep }).eq('id', talentProfileId).then(() => {});
    }
  }, [currentStep, userId, talentProfileId, profileData, selectedShoutoutTypes]);

  const enqueueOnboardingEmailFlow = async (userEmail: string, uid: string) => {
    try {
      const scheduleAt = new Date(Date.now() + 60 * 60 * 1000).toISOString();
      await supabase.from('user_email_flow_status').upsert({
        email: userEmail.toLowerCase(),
        user_id: uid,
        flow_id: 'aaaa5555-5555-5555-5555-555555555555',
        current_message_order: 0,
        next_email_scheduled_at: scheduleAt,
        flow_started_at: new Date().toISOString(),
        is_paused: false,
        source_url: window.location.href,
      }, { onConflict: 'email,flow_id' });
    } catch (flowErr) {
      console.warn('Could not enqueue onboarding email flow:', flowErr);
    }
  };

  // Handle authenticated user - create/get talent profile
  const handleUserAuthenticated = async (authUserId: string) => {
    setUserId(authUserId);
    setLoading(true);

    try {
      // Resolve partner referral token early so it applies to both new and existing profiles
      let partnerId: string | null = null;
      const refToken = partnerRef || safeStorage.getItem('partner_ref');
      if (refToken) {
        safeStorage.setItem('partner_ref', refToken);
        const { data: pid } = await supabase.rpc('resolve_partner_token', { p_token: refToken });
        if (pid) partnerId = pid;
      }

      // If admin onboarding token is present, link this user to the admin-created profile
      const onboardToken = adminToken || safeStorage.getItem('admin_onboard_token');
      let deadToken: 'expired' | 'invalid' | null = null;
      if (onboardToken) {
        safeStorage.setItem('admin_onboard_token', onboardToken);
        // An onboarding link (working or not) takes priority over a claim from
        // the page; that claim isn't kept for later.
        if (pendingClaimSlug) {
          safeStorage.removeItem(PAGE_CLAIM_STORAGE_KEY);
          setPendingClaimSlug(null);
        }

        // Resolve the page through the token-gated SECURITY DEFINER RPC. A
        // direct read of talent_profiles by token ran as the freshly signed-in
        // user, whose RLS cannot see an unclaimed INACTIVE page — the state
        // every admin-built page starts in — so the lookup came back empty and
        // the flow below created a duplicate profile instead of claiming the
        // built one. The token column itself is now always NULL (tokens live in
        // talent_onboarding_tokens, service-role only).
        const { data: adminProfile } = await supabase
          .rpc('get_onboarding_profile_by_token', { p_token: onboardToken });

        if (adminProfile) {
          if (adminProfile.onboarding_completed) {
            safeStorage.removeItem('admin_onboard_token');
            toast.success('Welcome back!');
            navigate('/dashboard');
            return;
          }

          // Already linked to this user — just resume
          if (adminProfile.user_id === authUserId) {
            setTalentProfileId(adminProfile.id);
            setLinkedPageId(adminProfile.id);
            if (adminProfile.skip_promo_video) setSkipPromoVideo(true);
            if (adminProfile.temp_full_name) setProfileData((prev) => ({ ...prev, fullName: adminProfile.temp_full_name || '' }));
            if (adminProfile.temp_avatar_url) setProfileData((prev) => ({ ...prev, avatarUrl: adminProfile.temp_avatar_url || '' }));
            if (adminProfile.username) setProfileData((prev) => ({ ...prev, username: adminProfile.username || '' }));
            if (adminProfile.pricing) setProfileData((prev) => ({ ...prev, pricing: adminProfile.pricing }));
            if (adminProfile.categories?.length) setProfileData((prev) => ({ ...prev, categories: adminProfile.categories }));
            if (adminProfile.targeting_category) setProfileData((prev) => ({ ...prev, targetingCategory: adminProfile.targeting_category }));
            if (adminProfile.selected_shoutout_types?.length) setSelectedShoutoutTypes(adminProfile.selected_shoutout_types);
            await enqueueOnboardingEmailFlow(email, authUserId);
            toast.success('Continuing your setup...');
            setCurrentStep(2);
            return;
          }

          // Link the admin-created profile to this user via RPC
          const normalizedEmail = email.toLowerCase().trim();
          const formattedPhone = toE164(phone);
          const { data: linkResult, error: linkError } = await supabase.rpc('link_talent_profile_to_user', {
            p_talent_id: adminProfile.id,
            p_user_id: authUserId,
            p_onboarding_token: onboardToken,
            p_full_name: adminProfile.temp_full_name || null,
            p_email: normalizedEmail || null,
            p_phone: formattedPhone,
            p_avatar_url: adminProfile.temp_avatar_url || null,
          });

          if (linkError || linkResult?.success !== true) {
            // link_talent_profile_to_user is the only path that can bind a page
            // to an account (it verifies the token and that the caller is
            // linking themselves). There is no client-side fallback: the token
            // column is not readable, so surface the failure and stop — going
            // on would let the creator "complete" a page they do not own.
            console.error('RPC link failed:', linkError ?? linkResult?.error);
            toast.error(linkResult?.error || 'Could not link this page to your account. Please try again or contact support@shoutout.us.');
            return;
          }

          setTalentProfileId(adminProfile.id);
          setLinkedPageId(adminProfile.id);
          if (adminProfile.skip_promo_video) setSkipPromoVideo(true);
          if (adminProfile.temp_full_name) setProfileData((prev) => ({ ...prev, fullName: adminProfile.temp_full_name || '' }));
          if (adminProfile.temp_avatar_url) setProfileData((prev) => ({ ...prev, avatarUrl: adminProfile.temp_avatar_url || '' }));
          if (adminProfile.username) setProfileData((prev) => ({ ...prev, username: adminProfile.username || '' }));
          if (adminProfile.pricing) setProfileData((prev) => ({ ...prev, pricing: adminProfile.pricing }));
          if (adminProfile.categories?.length) setProfileData((prev) => ({ ...prev, categories: adminProfile.categories }));
          if (adminProfile.targeting_category) setProfileData((prev) => ({ ...prev, targetingCategory: adminProfile.targeting_category }));
          if (adminProfile.selected_shoutout_types?.length) setSelectedShoutoutTypes(adminProfile.selected_shoutout_types);
          if (partnerId) {
            await supabase.from('talent_profiles').update({ referred_by_partner_id: partnerId }).eq('id', adminProfile.id);
          }

          await enqueueOnboardingEmailFlow(email, authUserId);
          toast.success('Account verified! Let\'s set up your profile.');
          setCurrentStep(2);
          return;
        }
        // Token not found or expired. A creator who already claimed or finished
        // their page (the token is consumed on completion) must still reach
        // their dashboard, so fall through to the by-user lookup below — but
        // remember the dead link and refuse to CREATE a fresh profile from it,
        // which is how duplicate pages were made.
        safeStorage.removeItem('admin_onboard_token');
        const { data: linkStatus } = await supabase.rpc('onboarding_link_status', { p_token: onboardToken });
        deadToken = linkStatus === 'expired' ? 'expired' : 'invalid';
      }

      // Claim from the page: the page they tapped "claim it free" on becomes
      // theirs (no onboarding link involved).
      if (pendingClaimSlug && !deadToken) {
        const claim = await startPageClaim(pendingClaimSlug);
        safeStorage.removeItem(PAGE_CLAIM_STORAGE_KEY);
        setPendingClaimSlug(null);
        if (claim.ok) {
          const p = claim.prefill;
          if (p.onboarding_completed) {
            toast.success('Welcome back!');
            navigate('/dashboard');
            return;
          }
          setTalentProfileId(claim.talentId);
          setLinkedPageId(claim.talentId);
          if (p.skip_promo_video) setSkipPromoVideo(true);
          setProfileData((prev) => ({
            ...prev,
            fullName: p.full_name || p.temp_full_name || prev.fullName,
            avatarUrl: p.temp_avatar_url || prev.avatarUrl,
            username: p.username || prev.username,
            pricing: typeof p.pricing === 'number' ? p.pricing : prev.pricing,
            categories: p.categories?.length ? (p.categories as TalentCategory[]) : prev.categories,
            targetingCategory: p.targeting_category || prev.targetingCategory,
          }));
          if (p.selected_shoutout_types?.length) setSelectedShoutoutTypes(p.selected_shoutout_types);
          // The referral and acquisition source, as the other signup paths keep
          // them (never replacing one the page already has).
          if (partnerId) {
            await supabase.from('talent_profiles').update({ referred_by_partner_id: partnerId })
              .eq('id', claim.talentId).is('referred_by_partner_id', null);
          }
          const claimUtm = utmSource || safeStorage.getItem('signup_utm') || '';
          if (claimUtm) {
            await supabase.from('talent_profiles').update({ signup_source: claimUtm })
              .eq('id', claim.talentId).is('signup_source', null);
          }
          await enqueueOnboardingEmailFlow(email, authUserId);
          toast.success(claim.resumed ? 'Continuing your setup...' : 'This page is yours! Let\'s set it up.');
          setCurrentStep(2);
          return;
        }
        toast.error(pageClaimErrorMessage(claim.error));
      }

      // Check for existing talent profile
      const { data: existingProfile } = await supabase
        .from('talent_profiles')
        .select('id, onboarding_completed, referred_by_partner_id')
        .eq('user_id', authUserId)
        .maybeSingle();

      if (existingProfile?.onboarding_completed) {
        toast.success('Welcome back!');
        navigate('/dashboard');
        return;
      }

      if (existingProfile) {
        setTalentProfileId(existingProfile.id);
        // Link to partner if not already linked
        if (partnerId && !existingProfile.referred_by_partner_id) {
          await supabase.from('talent_profiles').update({ referred_by_partner_id: partnerId }).eq('id', existingProfile.id);
        }
        // Stamp acquisition source on first touch (don't overwrite an existing one)
        const signupUtm = utmSource || safeStorage.getItem('signup_utm') || '';
        if (signupUtm) {
          await supabase.from('talent_profiles').update({ signup_source: signupUtm }).eq('id', existingProfile.id).is('signup_source', null);
        }
        await enqueueOnboardingEmailFlow(email, authUserId);
        // Check for saved progress and restore step
        const saved = safeStorage.getItem('talent_start_progress');
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            if (parsed.currentStep && parsed.currentStep > 1) {
              setCurrentStep(parsed.currentStep);
              toast.success('Continuing your setup...');
              return;
            }
          } catch (e) { /* ignore */ }
        }
        // Default to step 2 if no saved progress
        toast.success('Continuing your setup...');
        setCurrentStep(2);
        return;
      }

      if (deadToken) {
        toast.error(
          deadToken === 'expired'
            ? 'This onboarding link has expired. Reply to the email you received or contact support@shoutout.us for a new one.'
            : 'This onboarding link is not valid. Contact support@shoutout.us and we will send you a fresh one.'
        );
        return;
      }

      // Get user data
      // Own row (authUserId is the signed-in account): email is readable only
      // through get_my_user().
      const userData = await readMyUser<{ full_name: string | null; email: string | null }>('full_name, email');

      // Create new talent profile
      const insertData: any = {
        user_id: authUserId,
        category: 'other',
        bio: '', // a brand-new page: bio is NOT NULL; it's edited later in the dashboard
        pricing: 50,
        fulfillment_time_hours: 72,
        is_featured: false,
        is_active: false,
        total_orders: 0,
        fulfilled_orders: 0,
        average_rating: 0,
        admin_fee_percentage: defaultAdminFee,
        first_orders_promo_active: true,
        onboarding_completed: false,
        current_onboarding_step: 2,
      };
      if (partnerId) insertData.referred_by_partner_id = partnerId;
      const signupUtm = utmSource || safeStorage.getItem('signup_utm') || '';
      if (signupUtm) insertData.signup_source = signupUtm;

      const { data: newProfile, error: profileError } = await supabase
        .from('talent_profiles')
        .insert(insertData)
        .select()
        .single();

      if (profileError) {
        console.error('Profile creation error:', profileError);
        toast.error(`Failed to create profile: ${profileError.message || 'RLS policy may be blocking. Contact support.'}`);
        return;
      }

      setTalentProfileId(newProfile.id);
      if (userData?.full_name) {
        setProfileData((prev) => ({ ...prev, fullName: userData.full_name || '' }));
      }

      await enqueueOnboardingEmailFlow(email, authUserId);

      toast.success('Account verified! Let\'s set up your profile.');
      setCurrentStep(2);
    } catch (error: any) {
      console.error('Error setting up profile:', error);
      toast.error(error.message || 'Failed to set up profile');
    } finally {
      setLoading(false);
    }
  };

  // Username check
  useEffect(() => {
    if (!profileData.username || profileData.username.length < 3) {
      setUsernameAvailable(null);
      return;
    }
    setCheckingUsername(true);
    const timeout = setTimeout(async () => {
      try {
        setUsernameAvailable(await isUsernameAvailable(profileData.username, talentProfileId));
      } catch (e) {
        setUsernameAvailable(null);
      } finally {
        setCheckingUsername(false);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [profileData.username, talentProfileId]);

  // Unclaimed-profile typeahead: as the registering creator types their
  // display name or username, surface existing UNCLAIMED pages they can
  // claim instead of creating a duplicate.
  useEffect(() => {
    if (currentStep !== 2 || !claimField || claimedProfileId) {
      setClaimResults([]);
      return;
    }
    const query = (claimField === 'name' ? profileData.fullName : profileData.username).trim();
    if (query.length < 2) {
      setClaimResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const { data } = await supabase.rpc('search_unclaimed_talent_profiles', { p_query: query });
        setClaimResults((data as ClaimProfile[]) || []);
      } catch {
        setClaimResults([]);
      }
    }, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData.fullName, profileData.username, claimField, currentStep, claimedProfileId]);

  // "This is me." — pair the unclaimed profile (and its creator dashboard)
  // with the registering user, then prefill the form from the claimed page.
  const handleClaimProfile = async () => {
    if (!claimCandidate) return;
    setClaiming(true);
    try {
      // SO-003: claiming an unclaimed page requires the onboarding token that was
      // issued to the real creator (prevents taking over someone else's page).
      const onboardToken = adminToken || safeStorage.getItem('admin_onboard_token') || null;
      const { data, error } = await supabase.rpc('claim_talent_profile', {
        p_talent_id: claimCandidate.id,
        p_onboarding_token: onboardToken,
      });
      if (error) throw error;
      if (data?.success === false) {
        if (data?.error === 'ownership_verification_required') {
          // Offered by the duplicate prompt: offer to have staff send the claim link.
          if (claimFromPrompt) {
            setClaimLinkStep('ask');
            return;
          }
          toast.error(data?.message || 'To claim this page, open the onboarding link that was sent to you, or contact support.');
          return;
        }
        throw new Error(data?.error || 'Failed to claim profile');
      }

      setTalentProfileId(data.talent_id);
      setClaimedProfileId(data.talent_id);
      setProfileData(prev => ({
        ...prev,
        fullName: data.full_name || prev.fullName,
        username: data.username || prev.username,
        pricing: data.pricing || prev.pricing,
        categories: (data.categories?.length ? data.categories : prev.categories),
        targetingCategory: data.targeting_category || prev.targetingCategory,
        avatarUrl: data.avatar_url || prev.avatarUrl,
      }));
      closeClaimModal();
      setClaimResults([]);
      setClaimField(null);
      toast.success(`Welcome back${data.full_name ? `, ${data.full_name}` : ''}! Your page is now linked to your account.`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to claim profile');
    } finally {
      setClaiming(false);
    }
  };

  // A "not mine" answer is kept until it's saved: tried three times in the
  // background, and again the next time /start opens for that page. It never
  // holds up signup.
  const saveNotMine = useCallback(async (talentId: string, search: DuplicateSearch) => {
    const answer = { talentId, ...search };
    updatePendingDuplicateAnswer(answer, true);
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await recordSignupDuplicate(talentId, 'not_mine', search.fullName, search.username);
        updatePendingDuplicateAnswer(answer, false);
        return;
      } catch (err) {
        if (attempt === 3) console.error('Could not record the duplicate answer yet:', err);
        else await new Promise((resolve) => setTimeout(resolve, 2000 * attempt));
      }
    }
  }, []);

  useEffect(() => {
    if (!talentProfileId) return;
    for (const pending of readPendingDuplicateAnswers()) {
      if (pending.talentId === talentProfileId) {
        void saveNotMine(pending.talentId, { fullName: pending.fullName, username: pending.username });
      }
    }
  }, [talentProfileId, saveNotMine]);

  const closeClaimModal = () => {
    setClaimCandidate(null);
    setClaimFromPrompt(null);
    setClaimLinkStep(null);
  };

  // "Is this your page?" → "Claim it": the usual claim flow for that page.
  const handleDuplicateClaim = (page: SignupDuplicatePage) => {
    if (!duplicatePrompt) return;
    setClaimFromPrompt({ fullName: duplicatePrompt.fullName, username: duplicatePrompt.username });
    setClaimLinkStep(null);
    setDuplicatePrompt(null);
    setClaimCandidate(page);
  };

  // No claim link for a page the prompt offered: staff check it's them and
  // send the link (F11 review list).
  const handleRequestClaimLink = async () => {
    if (!claimCandidate || !claimFromPrompt || !talentProfileId) return;
    setClaimLinkStep('sending');
    try {
      await recordSignupDuplicate(
        talentProfileId,
        'claim_requested',
        claimFromPrompt.fullName,
        claimFromPrompt.username,
        claimCandidate.id
      );
      setDuplicateAnsweredFor(talentProfileId);
      setClaimLinkStep('sent');
    } catch (err) {
      console.error('Claim link request failed:', err);
      setClaimLinkStep('ask');
      toast.error('We couldn’t send that. Email support@shoutout.us and we’ll help.');
    }
  };

  // Go on with a new page. A "not mine" answer is noted for staff first.
  const continueWithNewPage = async (search: DuplicateSearch, notMine: boolean) => {
    setDuplicatePrompt(null);
    closeClaimModal();
    setDuplicateAnsweredFor(talentProfileId);
    if (notMine && talentProfileId) void saveNotMine(talentProfileId, search);
    await submitProfile(true);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedEmail = email.toLowerCase().trim();
    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: normalizedEmail, checkEmailOnly: true },
      });

      if (error || data?.error || data?.success === false) {
        console.error('Email check error:', error);
        // The server answered with a reason (e.g. the phone on file is in a
        // country we can't text yet): show it. Only a failed request falls
        // back to the phone step.
        if (data?.error || data?.success === false || (error as any)?.name === 'FunctionsHttpError') {
          const message = await otpErrorMessage(error, data);
          toast.error(message);
          trackClaimStep(claimFunnel, 'claim_verify_failed', { category: claimFailureCategory(message) });
        } else {
          // no code went to a phone on file: on to the phone step
          trackClaimStep(claimFunnel, 'claim_email_submitted', { category: 'new_user' });
          setAuthStep('phone');
        }
        return;
      }

      trackClaimStep(claimFunnel, 'claim_email_submitted', {
        category: data?.sentToExistingPhone ? 'existing_user' : 'new_user',
      });
      if (data?.sentToExistingPhone) {
        trackClaimStep(claimFunnel, 'claim_verify_sent', { channel: 'sms' });
        // Phone already on file: OTP was sent server-side. The server no longer
        // returns the raw phone (only a masked hint), so skip to the OTP step
        // and let verify/resend resolve the phone by email.
        setPhoneOnFile(true);
        setExistingUserPhone(data.phoneHint || null);
        toast.success('We sent a code to your phone!');
        setAuthStep('otp');
        setOtpCooldown(60);
        setTimeout(() => otpInputRef.current?.focus(), 100);
      } else {
        setPhoneOnFile(false);
        setAuthStep('phone');
      }
    } catch (error: any) {
      console.error('Email check error:', error);
      // no code went to a phone on file: on to the phone step
      trackClaimStep(claimFunnel, 'claim_email_submitted', { category: 'new_user' });
      setAuthStep('phone');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formattedPhone = toE164(phone);
    if (!formattedPhone) {
      toast.error(PHONE_FORMAT_ERROR);
      return;
    }

    setLoading(true);
    const normalizedEmail = email.toLowerCase().trim();

    try {
      const { data, error } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: normalizedEmail, phone: formattedPhone },
      });

      if (error || data?.error || data?.success === false) throw new Error(await otpErrorMessage(error, data));

      trackClaimStep(claimFunnel, 'claim_verify_sent', { channel: 'sms' });
      toast.success('Verification code sent!');
      setAuthStep('otp');
      setOtpCooldown(60);
      setTimeout(() => otpInputRef.current?.focus(), 100);
    } catch (error: any) {
      console.error('Send OTP error:', error);
      trackClaimStep(claimFunnel, 'claim_verify_failed', { category: claimFailureCategory(error?.message) });
      toast.error(error.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (otpCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    const normalizedEmail = email.toLowerCase().trim();
    // Only trust a raw phone the user actually typed. On the phone-on-file path
    // we don't have it, so leave it undefined and let the server resolve by email.
    const rawPhone = toE164(phone);
    const formattedPhone = phoneOnFile ? undefined : (rawPhone || undefined);

    // A failure after the code was accepted (setting up the page) isn't a
    // failed verification.
    let verified = false;
    try {
      const { data, error } = await supabase.functions.invoke('verify-registration-otp', {
        body: { email: normalizedEmail, phone: formattedPhone, code: otpCode },
      });

      if (error || data?.error || data?.success === false) throw new Error(await otpErrorMessage(error, data, 'Invalid verification code'));
      verified = true;
      trackClaimStep(claimFunnel, 'claim_verify_ok');

      if (data?.session?.access_token && data?.session?.refresh_token) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });

        if (sessionError) {
          console.error('Session error:', sessionError);
          throw new Error('Failed to establish session');
        }
      } else {
        throw new Error('No session returned from verification');
      }

      // Create or update user record to be a talent. Only set phone when we have
      // a real typed number; never overwrite an existing phone with a bad value.
      const insertRecord: any = {
        id: data.user.id,
        email: normalizedEmail,
        user_type: 'talent',
        created_at: new Date().toISOString(),
      };
      if (rawPhone) insertRecord.phone = rawPhone;
      const { error: userInsertError } = await supabase.from('users').insert(insertRecord);

      if (userInsertError) {
        const updateRecord: any = { user_type: 'talent' };
        if (rawPhone) updateRecord.phone = rawPhone;
        const { error: userUpdateError } = await supabase.from('users').update(updateRecord).eq('id', data.user.id);
        if (userUpdateError) console.error('Failed to update user type:', userUpdateError);
      }

      toast.success(data.isLogin ? 'Welcome back!' : 'Account verified!');
      await handleUserAuthenticated(data.user.id);
    } catch (error: any) {
      console.error('OTP verification error:', error);
      if (!verified) trackClaimStep(claimFunnel, 'claim_verify_failed', { category: claimFailureCategory(error?.message) });
      toast.error(error.message || 'Invalid verification code');
      setOtpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpCooldown > 0) return;

    setLoading(true);
    const normalizedEmail = email.toLowerCase().trim();

    try {
      // For a phone on file, resend via checkEmailOnly so the server re-sends to
      // the stored phone (normal mode needs a raw phone we no longer hold).
      const body = phoneOnFile
        ? { email: normalizedEmail, checkEmailOnly: true }
        : { email: normalizedEmail, phone: toE164(phone) || undefined };
      const { data, error } = await supabase.functions.invoke('send-registration-otp', {
        body,
      });
      if (error || data?.error || data?.success === false) throw new Error(await otpErrorMessage(error, data, 'Failed to resend code'));

      trackClaimStep(claimFunnel, 'claim_verify_sent', { channel: 'sms' });
      toast.success('New code sent!');
      setOtpCooldown(60);
      setOtpCode('');
    } catch (error: any) {
      trackClaimStep(claimFunnel, 'claim_verify_failed', { category: claimFailureCategory(error?.message) });
      toast.error(error.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submitProfile();
  };

  // answered: the creator just answered the duplicate prompt (state set in
  // the same click isn't visible here yet).
  const submitProfile = async (answered = false) => {
    if (!talentProfileId) return toast.error('Profile not found');
    if (!profileData.fullName) return toast.error('Full name required');
    if (!profileData.username) return toast.error('Username required');
    // Is there an unclaimed page with exactly this name or username? Asked
    // before "Username taken", since an unclaimed page holding the username
    // is the strongest sign. Asked once per page; if the check can't run,
    // signup goes on as before. Not asked when they're already finishing a
    // page that was built for them: claimed here, or opened with a working
    // onboarding link (an expired or wrong link doesn't count).
    const onBuiltPage = talentProfileId === claimedProfileId || talentProfileId === linkedPageId;
    if (!answered && !onBuiltPage && duplicateAnsweredFor !== talentProfileId) {
      const search = { fullName: profileData.fullName.trim(), username: profileData.username.trim() };
      setLoading(true);
      const found = await checkSignupDuplicates(search.fullName, search.username);
      setLoading(false);
      if (found && found.unclaimed.length > 0) {
        setDuplicatePrompt({ ...found, ...search });
        return;
      }
    }
    if (usernameAvailable === false) return toast.error('Username taken');
    const fieldsError = startProfileFieldsError(profileData);
    if (fieldsError) return toast.error(fieldsError);
    setLoading(true);
    try {
      const { error } = await supabase
        .from('talent_profiles')
        // No bio at signup: the save leaves the page's bio as it is (Nicky, 27 Oct 2026).
        .update(startProfileUpdate(profileData, selectedShoutoutTypes))
        .eq('id', talentProfileId);

      if (isUsernameTakenError(error)) {
        setUsernameAvailable(false);
        toast.error('Username taken');
        return;
      }
      if (error) throw error;

      if (userId) {
        await supabase.from('users').update({
          full_name: profileData.fullName,
          avatar_url: profileData.avatarUrl || undefined,
        }).eq('id', userId);
      }
      toast.success('Profile saved!');
      if (skipPromoVideo || (await ensureOnboardingV2())) {
        await supabase.from('talent_profiles').update({ is_active: true }).eq('id', talentProfileId);
        setCurrentStep(4);
      } else {
        setCurrentStep(3);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleVideoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!promoVideo) return toast.error('Please upload a video');
    if (!talentProfileId) return toast.error('Profile not found. Please refresh and try again.');

    console.log('[video] submit start', { size: promoVideo.size, talentProfileId });
    setVideoUploading(true);
    try {
      const result = await uploadVideoToWasabi(promoVideo, talentProfileId);
      console.log('[video] upload result', result);
      if (!result.success || !result.videoUrl) throw new Error(result.error || 'Video upload failed');

      console.log('[video] updating talent_profiles row...');
      const { error: updateError } = await supabase
        .from('talent_profiles')
        .update({ promo_video_url: result.videoUrl, is_active: true })
        .eq('id', talentProfileId);

      if (updateError) {
        console.error('[video] talent_profiles update error', updateError);
        throw updateError;
      }
      console.log('[video] talent_profiles updated');

      // Complete the talent_signup email flow (stop sending reminders) - non-blocking
      (async () => {
        try {
          const now = new Date().toISOString();
          await supabase.from('user_email_flow_status')
            .update({ flow_completed_at: now, updated_at: now })
            .eq('email', email.toLowerCase())
            .eq('flow_id', 'aaaa5555-5555-5555-5555-555555555555')
            .is('flow_completed_at', null);
        } catch (flowErr) {
          console.warn('[video] could not complete onboarding email flow:', flowErr);
        }
      })();

      toast.success('Video uploaded!');
      setCurrentStep(4);
    } catch (error: any) {
      console.error('[video] submit error', error);
      toast.error(error?.message || 'Upload failed');
    } finally {
      setVideoUploading(false);
    }
  };

  /**
   * Is this signup on onboarding v2? The database decides (switch, QA list) and
   * records it for the page. Only a definite answer is cached: an error means
   * the page isn't linked to this account yet, so it's asked again later.
   */
  const ensureOnboardingV2 = useCallback(async (): Promise<boolean> => {
    if (onboardingV2 !== null) return onboardingV2;
    if (!talentProfileId || !userId) return false;
    const { data, error } = await supabase.rpc('creator_onboarding_begin', { p_talent_id: talentProfileId });
    if (error) return false;
    setOnboardingV2(!!data);
    return !!data;
  }, [onboardingV2, talentProfileId, userId]);

  useEffect(() => {
    if (currentStep >= 2 && talentProfileId && userId && onboardingV2 === null) void ensureOnboardingV2();
  }, [currentStep, talentProfileId, userId, onboardingV2, ensureOnboardingV2]);

  // On the last step (signed in by now), read the Terms version the box stands
  // for. One retry. Nothing waits for it.
  useEffect(() => {
    if (currentStep !== 4 || !userId) return;
    let cancelled = false;
    void (async () => {
      let version = await getCreatorTermsSignupVersion();
      if (!version) version = await getCreatorTermsSignupVersion();
      if (!cancelled && version) termsVersionRef.current = version;
    })();
    return () => {
      cancelled = true;
    };
  }, [currentStep, userId]);

  // v2 has no promo video step: progress saved on it moves on to the last step.
  useEffect(() => {
    if (onboardingV2 && currentStep === 3) setCurrentStep(4);
  }, [onboardingV2, currentStep]);

  const handleBioLinkComplete = async (opts?: { confirmBioLink?: boolean }) => {
    if (!talentProfileId || !termsAgreed) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from('talent_profiles')
        .update({ onboarding_completed: true, is_active: true })
        .eq('id', talentProfileId);

      if (error) throw error;

      // The box is ticked: record the agreement to the Terms in force now
      // (the database picks the version). Two tries. If both fail, finishing
      // still goes ahead and the workspace retries for the version read when
      // this step opened, or read now if that hadn't landed yet
      // (creatorTerms.ts); without a version there's nothing safe to retry.
      // Staff accounts are refused and never retried.
      let termsRecorded = false;
      for (let attempt = 0; attempt < 2 && !termsRecorded; attempt += 1) {
        try {
          await acceptCreatorTermsAtSignup(null);
          termsRecorded = true;
        } catch (termsErr: any) {
          console.warn('Could not record the Terms acceptance:', termsErr?.message);
          if (isTermsRefusal(termsErr)) break;
          if (attempt === 0) {
            await new Promise((r) => setTimeout(r, 800));
          } else if (userId) {
            const pinned = termsVersionRef.current ?? (await getCreatorTermsSignupVersion());
            if (pinned) markSignupTermsPending(userId, pinned);
          }
        }
      }

      // v2 "I've added it": record when. A failure here doesn't block finishing;
      // the must-do stays open on Home and can be tapped again there.
      if (onboardingV2 && opts?.confirmBioLink) {
        const { error: bioErr } = await supabase.rpc('creator_onboarding_confirm_bio_link', {
          p_talent_id: talentProfileId,
        });
        if (bioErr) console.warn('Could not record the bio link:', bioErr.message);
      }

      // `email` is only set when this visit typed it on step 1. A creator who
      // resumed setup (reload, another device, claim link) has it blank, so
      // fall back to the signed-in account's own address: the stored session
      // first (no network), then a fresh lookup.
      let accountEmail = email.trim();
      if (!accountEmail) {
        const { data: { session } } = await supabase.auth.getSession();
        accountEmail = session?.user?.email?.trim() || '';
      }
      if (!accountEmail) {
        const { data: { user: authUser }, error: authErr } = await supabase.auth.getUser();
        if (authErr) console.warn('Could not look up the account email:', authErr.message);
        accountEmail = authUser?.email?.trim() || '';
      }

      try {
        await supabase.functions.invoke('onboarding-complete-notification', {
          body: { talentId: talentProfileId, talentName: profileData.fullName, email: accountEmail },
        });
      } catch (e) {
        console.warn('Failed to send admin notification:', e);
      }

      // Welcome email with their ShoutOut Fans URL. The server builds it and
      // sends it to the account's own address (send-app-email).
      try {
        const { error: welcomeErr } = await supabase.functions.invoke('send-app-email', {
          body: { kind: 'welcome', talent_id: talentProfileId },
        });
        if (welcomeErr) throw welcomeErr;
      } catch (emailErr) {
        console.warn('Could not send welcome email:', emailErr);
      }

      safeStorage.removeItem('talent_start_progress');
      safeStorage.removeItem('talent_start_email');
      safeStorage.removeItem('admin_onboard_token');

      toast.success('🎉 Welcome to ShoutOut! Setting up your dashboard...');
      setTimeout(() => {
        // Relative redirect — we're already on creator.shoutout.us.
        window.location.href = `/dashboard?token=${talentProfileId}${accountEmail ? `&email=${encodeURIComponent(accountEmail)}` : ''}&userId=${userId}&tab=${onboardingV2 ? 'welcome' : 'customize'}`;
      }, 1500);
    } catch (error: any) {
      toast.error(error.message || 'Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  const formatSignupDate = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const days = Math.floor((now.getTime() - d.getTime()) / 86400000);
    if (days <= 0) return 'Joined today';
    if (days === 1) return 'Joined yesterday';
    if (days < 7) return `Joined ${days} days ago`;
    return `Joined ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  const fomoCurrent = fomoSignups[fomoIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#0f172a] to-slate-900">
      {/* Header */}
      <div className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <a href="https://shoutout.us">
              <Logo size="sm" theme="dark" />
            </a>
            {referrer && (
              <div className="flex items-center gap-2 sm:gap-3">
                {referrer.avatar ? (
                  <img
                    src={referrer.avatar}
                    alt={referrer.name}
                    className="h-9 w-9 sm:h-11 sm:w-11 rounded-full object-cover ring-2 ring-white/20"
                  />
                ) : (
                  <div className="h-9 w-9 sm:h-11 sm:w-11 rounded-full bg-white/10 ring-2 ring-white/20 flex items-center justify-center text-white font-semibold">
                    {(referrer.name || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <p className="text-xs sm:text-sm text-gray-200">
                  Join <span className="font-semibold text-white">{referrer.name}</span> on ShoutOut.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Step 0: Intro / Platform Overview */}
      {currentStep === 0 && (
        <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8 lg:py-10">
          <div className="glass rounded-2xl shadow-2xl overflow-hidden bg-white/5 backdrop-blur-sm border border-white/10">
            <div className="p-6 sm:p-8 lg:p-10">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-sm font-semibold mb-5">
                  <SparklesIcon className="w-4 h-4" />
                  For Free-Speech Creators
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
                  Transform your audience into a{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-cyan-500">monetizable, uncancellable</span>{' '}
                  engine that fuels your growth.
                </h2>
                <p className="text-lg text-gray-400">
                  Join the biggest voices in the free speech movement.
                </p>
              </div>

              {featuredTalent.length > 0 && (
                <div className="relative mb-8 -mx-6 sm:-mx-8 lg:-mx-10 overflow-hidden">
                  <div className="overflow-hidden py-3">
                    <div
                      ref={introCarouselRef}
                      className="flex gap-4"
                      style={{ width: 'max-content' }}
                    >
                      {[...featuredTalent, ...featuredTalent, ...featuredTalent, ...featuredTalent].map((talent, i) => (
                        <div key={i} className="flex flex-col items-center flex-shrink-0" style={{ width: '88px' }}>
                          <div className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-xl overflow-hidden bg-gradient-to-br from-emerald-400 to-cyan-400 p-[2px]">
                            <img
                              src={talent.avatar}
                              alt={talent.name}
                              className="w-full h-full object-cover rounded-[10px]"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          </div>
                          <p className="text-[10px] sm:text-xs text-gray-500 font-medium mt-1.5 text-center truncate w-full">{talent.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="text-center mb-8">
                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/20 text-sm font-semibold text-gray-200">
                  Ditch Linktree for the only link in bio built for free-speech creators
                </span>
              </div>

              <div className="space-y-3 mb-8">
                <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-xl border border-emerald-500/30">
                  <VideoCameraIcon className="w-6 h-6 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <p className="font-semibold text-lg text-white">Sell personalized video shoutouts directly from your shoutout.fans link</p>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                  <CheckIcon className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-300">Engage followers through fan driven messages and donations</p>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                  <CheckIcon className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-300">Add pre-vetted, pre-paid sponsors to your shoutout.fans bio page</p>
                </div>

                <div className="flex items-center gap-3 p-4 bg-amber-500/20 rounded-xl border border-amber-500/30">
                  <CurrencyDollarIcon className="w-6 h-6 text-amber-400 flex-shrink-0" />
                  <p className="text-amber-200 font-semibold">Earn $60–$120 per 100 clicks by adding pre-paid sponsors to your link.</p>
                </div>

                {creatorsEarned && (
                  <div className="flex items-start gap-3 p-3 rounded-xl">
                    <CheckIcon className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-300">
                      <span className="text-white font-semibold">{creatorsEarned.toLocaleString('en-US')}+ creators</span> have
                      already earned on ShoutOut
                    </p>
                  </div>
                )}

                <div className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                  <SparklesIcon className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-300"><span className="text-purple-400 font-medium">Coming soon:</span> sell merch like never before</p>
                </div>
              </div>

              <div className="text-center mb-8 p-4 bg-white/5 rounded-xl">
                <p className="text-lg text-white font-semibold">
                  All inside one, powerful link that averages over a{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-cyan-500 font-bold">30% click-through rate</span>.
                </p>
              </div>

              <button
                onClick={() => setCurrentStep(1)}
                className="w-full py-4 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold text-lg rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25"
              >
                Get Started
                <ArrowRightIcon className="w-5 h-5" />
              </button>

              <p className="text-center text-xs text-gray-400 mt-4">
                Already have an account? Click Get Started to log in.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Wizard Steps 1-4 */}
      {currentStep >= 1 && (() => {
        const activeSteps = skipPromoVideo || onboardingV2 ? STEPS.filter((s) => s.id !== 3) : STEPS;
        const visibleIndex = activeSteps.findIndex((s) => s.id === currentStep) + 1;
        return (
          <div className="max-w-4xl mx-auto px-4 py-4 sm:py-6 lg:py-8">
            {/* Mobile Progress Bar */}
            <div className="lg:hidden mb-4">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Step {visibleIndex} of {activeSteps.length}</span>
                  <span className="text-xs text-emerald-400">{activeSteps.find((s) => s.id === currentStep)?.name}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-2">
                  {activeSteps.map((step) => {
                    const isCompleted = currentStep > step.id;
                    const isCurrent = currentStep === step.id;
                    return (
                      <div
                        key={step.id}
                        className={`flex-1 h-1.5 rounded-full transition-all ${
                          isCompleted ? 'bg-emerald-500' : isCurrent ? 'bg-gradient-to-r from-emerald-500 to-cyan-500' : 'bg-white/10'
                        }`}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-[280px,1fr] gap-6 lg:gap-8">
              {/* Sidebar */}
              <div className="hidden lg:block lg:sticky lg:top-6 lg:self-start">
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-4 sm:p-6">
                  <h3 className="text-base font-semibold text-white mb-4">Your Progress</h3>
                  <nav className="space-y-1.5">
                    {activeSteps.map((step) => {
                      const Icon = step.icon;
                      const isCompleted = currentStep > step.id;
                      const isCurrent = currentStep === step.id;
                      return (
                        <div
                          key={step.id}
                          className={`flex items-center gap-3 p-2.5 rounded-xl transition-all ${
                            isCurrent ? 'bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30' : ''
                          } ${isCompleted ? 'opacity-60' : ''}`}
                        >
                          <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                            isCompleted ? 'bg-emerald-500' : isCurrent ? 'bg-gradient-to-r from-emerald-500 to-cyan-500' : 'bg-white/10'
                          }`}>
                            {isCompleted ? <CheckIcon className="w-4 h-4 text-white" /> : <Icon className="w-4 h-4 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isCurrent ? 'text-white' : 'text-gray-300'}`}>{step.name}</p>
                            <p className="text-xs text-gray-500 truncate">{step.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </nav>
                  <div className="mt-4 p-3 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                    <p className="text-xs text-emerald-300">
                      <SparklesIcon className="w-4 h-4 inline mr-1" />
                      Progress auto-saved. Continue anytime!
                    </p>
                  </div>
                </div>
              </div>

              {/* Main Content */}
              <div className="rounded-2xl shadow-2xl min-w-0 overflow-hidden" style={{ background: '#ffffff' }}>
                {/* Step 1: Email-first OTP Auth */}
                {currentStep === 1 && (
                  <div className="p-6 sm:p-8 lg:p-10">
                    <div className="max-w-md mx-auto">
                      {authStep === 'email' && (
                        <form onSubmit={handleEmailSubmit} className="space-y-5">
                          <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                              <DevicePhoneMobileIcon className="w-8 h-8 text-white" />
                            </div>
                            {pendingClaimSlug ? (
                              <>
                                <h2 className="text-2xl font-bold text-gray-900">Claim my page</h2>
                                <p className="mt-2 inline-block max-w-full break-all rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">
                                  shoutout.fans/{pendingClaimSlug}
                                </p>
                                <p className="text-gray-600 text-sm mt-2">
                                  Enter your email to sign up or log in. The page is yours as soon as you do.
                                </p>
                              </>
                            ) : (
                              <>
                                <h2 className="text-2xl font-bold text-gray-900">Get Started</h2>
                                <p className="text-gray-600 text-sm mt-1">
                                  Enter your email to sign up or log in
                                </p>
                              </>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Email Address
                            </label>
                            <input
                              type="email"
                              required
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-gray-900"
                              placeholder="Enter your email"
                              autoFocus
                            />
                          </div>

                          <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {loading ? 'Checking...' : 'Continue'}
                            <ArrowRightIcon className="w-4 h-4" />
                          </button>
                        </form>
                      )}

                      {authStep === 'phone' && (
                        <form onSubmit={handlePhoneSubmit} className="space-y-5">
                          <div className="text-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Add Your Phone</h2>
                            <p className="text-gray-600 text-sm mt-1">
                              We'll send a verification code to your phone
                            </p>
                          </div>

                          <div className="bg-gray-50 rounded-lg p-3 mb-4">
                            <p className="text-sm text-gray-600">
                              Email: <span className="font-medium">{email}</span>
                              <button
                                type="button"
                                onClick={() => { setAuthStep('email'); setPhoneOnFile(false); setExistingUserPhone(null); }}
                                className="ml-2 text-emerald-600 hover:text-emerald-700 text-sm"
                              >
                                Change
                              </button>
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Phone Number
                            </label>
                            <input
                              type="tel"
                              required
                              value={phone}
                              onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                              className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-gray-900"
                              placeholder="(555) 123-4567"
                              autoFocus
                            />
                            <p className="text-xs text-gray-500 mt-1">We'll send you a verification code. {PHONE_HELP_TEXT}</p>
                          </div>

                          <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            <DevicePhoneMobileIcon className="w-5 h-5" />
                            {loading ? 'Sending code...' : 'Send Verification Code'}
                          </button>
                        </form>
                      )}

                      {authStep === 'otp' && (
                        <form onSubmit={handleOtpSubmit} className="space-y-5">
                          <div className="text-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Enter Code</h2>
                            <p className="text-gray-600 text-sm mt-1">
                              We sent a 6-digit code to
                            </p>
                            <p className="text-emerald-600 font-medium">
                              {existingUserPhone
                                ? `***-***-${existingUserPhone.slice(-4)}`
                                : phone
                              }
                            </p>
                          </div>

                          <div>
                            <input
                              ref={otpInputRef}
                              type="text"
                              inputMode="numeric"
                              maxLength={6}
                              required
                              value={otpCode}
                              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-center text-2xl tracking-[0.5em] font-mono text-gray-900"
                              placeholder="000000"
                              autoComplete="one-time-code"
                              autoFocus
                            />
                          </div>

                          <button
                            type="submit"
                            disabled={loading || otpCode.length !== 6}
                            className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {loading ? 'Verifying...' : 'Verify & Continue'}
                            <ArrowRightIcon className="w-4 h-4" />
                          </button>

                          <div className="text-center space-y-2">
                            <button
                              type="button"
                              onClick={handleResendOtp}
                              disabled={otpCooldown > 0 || loading}
                              className="text-sm text-emerald-600 hover:text-emerald-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                              {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : "Didn't receive the code? Resend"}
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                setAuthStep('phone');
                                setOtpCode('');
                                setExistingUserPhone(null);
                                setPhoneOnFile(false);
                              }}
                              className="block w-full text-sm text-gray-500 hover:text-gray-700"
                            >
                              Use a different phone number
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 2: Profile + Categories */}
                {currentStep === 2 && (
                  <div className="p-6 sm:p-8 lg:p-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Create Your ShoutOut Profile</h2>
                    <p className="text-gray-600 mb-6">For your Link In Bio and your video ShoutOut orders.</p>

                    <form onSubmit={handleProfileSubmit} className="space-y-5">
                      <div>
                        <ImageUpload
                          currentImageUrl={profileData.avatarUrl}
                          onImageUploaded={(url) => setProfileData({ ...profileData, avatarUrl: url })}
                          uploadPath="talent-avatars"
                        />
                      </div>

                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                        <input
                          type="text"
                          required
                          value={profileData.fullName}
                          onChange={(e) => { setProfileData({ ...profileData, fullName: e.target.value }); setClaimField('name'); }}
                          onFocus={() => setClaimField('name')}
                          onBlur={() => setTimeout(() => setClaimField(f => (f === 'name' ? null : f)), 200)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500"
                          placeholder="John Smith"
                        />
                        {claimField === 'name' && claimResults.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 rounded-xl shadow-2xl border border-gray-200 bg-white overflow-hidden max-h-64 overflow-y-auto">
                            <p className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 bg-gray-50">Is one of these you? Claim my page</p>
                            {claimResults.map((p) => (
                              <button
                                key={p.id}
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); setClaimCandidate(p); }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-emerald-50 transition-colors text-left"
                              >
                                {p.avatar_url ? (
                                  <img src={p.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                                  {p.username && <p className="text-xs text-gray-500 truncate">shoutout.fans/{p.username}</p>}
                                </div>
                                <span className="flex-shrink-0 px-3 py-1 text-xs font-semibold rounded-full bg-emerald-500 text-white">Claim</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                        <div className="flex">
                          {/* The page lives at shoutout.fans/<username> (Nicky, 27 Oct 2026). */}
                          <span className="inline-flex flex-shrink-0 items-center px-3 rounded-l-xl bg-gray-100 text-gray-500 text-sm border border-r-0 border-gray-300">
                            shoutout.fans/
                          </span>
                          <input
                            type="text"
                            required
                            value={profileData.username}
                            onChange={(e) => { setProfileData({ ...profileData, username: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }); setClaimField('username'); }}
                            onFocus={() => setClaimField('username')}
                            onBlur={() => setTimeout(() => setClaimField(f => (f === 'username' ? null : f)), 200)}
                            className={`min-w-0 flex-1 px-4 py-3 border rounded-r-xl focus:ring-2 ${
                              usernameAvailable === false ? 'border-red-500 focus:ring-red-500' :
                              usernameAvailable === true ? 'border-emerald-500 focus:ring-emerald-500' :
                              'border-gray-300 focus:ring-emerald-500'
                            }`}
                            placeholder="yourname"
                          />
                        </div>
                        {usernameAvailable === false && <p className="text-xs text-red-500 mt-1">Username taken</p>}
                        {usernameAvailable === true && <p className="text-xs text-emerald-500 mt-1">✓ Available</p>}
                        {checkingUsername && <p className="text-xs text-gray-500 mt-1">Checking...</p>}
                        {claimField === 'username' && claimResults.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 rounded-xl shadow-2xl border border-gray-200 bg-white overflow-hidden max-h-64 overflow-y-auto">
                            <p className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 bg-gray-50">Is one of these you? Claim my page</p>
                            {claimResults.map((p) => (
                              <button
                                key={p.id}
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); setClaimCandidate(p); }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-emerald-50 transition-colors text-left"
                              >
                                {p.avatar_url ? (
                                  <img src={p.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                                  {p.username && <p className="text-xs text-gray-500 truncate">shoutout.fans/{p.username}</p>}
                                </div>
                                <span className="flex-shrink-0 px-3 py-1 text-xs font-semibold rounded-full bg-emerald-500 text-white">Claim</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>


                      <div ref={categoryDropdownRef} className="relative" style={{ zIndex: categoryDropdownOpen ? 50 : 'auto' }}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Categories * (up to 3)</label>
                        <button
                          type="button"
                          onClick={() => { setCategoryDropdownOpen(!categoryDropdownOpen); setOccasionDropdownOpen(false); }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 bg-white text-left flex items-center justify-between"
                        >
                          <span className={profileData.categories.length > 0 ? 'text-gray-900' : 'text-gray-400'}>
                            {profileData.categories.length > 0
                              ? profileData.categories.map((c) => CATEGORY_OPTIONS.find((o) => o.value === c)?.label).join(', ')
                              : 'Select categories...'}
                          </span>
                          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${categoryDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {categoryDropdownOpen && (
                          <div className="absolute z-50 w-full mt-1 rounded-xl shadow-2xl max-h-60 overflow-y-auto border border-white/20" style={{ backgroundColor: 'rgba(15, 23, 42, 0.97)', backdropFilter: 'blur(24px)' }}>
                            {CATEGORY_OPTIONS.map((opt) => {
                              const isSelected = profileData.categories.includes(opt.value);
                              const isDisabled = !isSelected && profileData.categories.length >= 3;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  disabled={isDisabled}
                                  onClick={() => {
                                    const updated = isSelected
                                      ? profileData.categories.filter((c) => c !== opt.value)
                                      : [...profileData.categories, opt.value];
                                    setProfileData({ ...profileData, categories: updated.slice(0, 3) });
                                  }}
                                  className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/10'} ${isSelected ? 'bg-emerald-500/15' : ''}`}
                                >
                                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-500'}`}>
                                    {isSelected && <CheckIcon className="w-3.5 h-3.5 text-white" />}
                                  </div>
                                  <span className="text-gray-200 text-sm">{opt.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div ref={contentTypeDropdownRef} className="relative" style={{ zIndex: contentTypeDropdownOpen ? 50 : 'auto' }}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Content Type *</label>
                        <p className="text-xs text-gray-500 mb-1">Your primary content — we use this to match you with the right pre-paid sponsors.</p>
                        <button
                          type="button"
                          onClick={() => { setContentTypeDropdownOpen(!contentTypeDropdownOpen); setCategoryDropdownOpen(false); setOccasionDropdownOpen(false); }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 bg-white text-left flex items-center justify-between"
                        >
                          <span className={profileData.targetingCategory ? 'text-gray-900' : 'text-gray-400'}>
                            {profileData.targetingCategory || 'Select your content type...'}
                          </span>
                          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${contentTypeDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {contentTypeDropdownOpen && (
                          <div className="absolute z-50 w-full mt-1 rounded-xl shadow-2xl max-h-60 overflow-y-auto border border-white/20" style={{ backgroundColor: 'rgba(15, 23, 42, 0.97)', backdropFilter: 'blur(24px)' }}>
                            {TARGETING_CATEGORIES.map((opt) => {
                              const isSelected = profileData.targetingCategory === opt;
                              return (
                                <button
                                  key={opt}
                                  type="button"
                                  onClick={() => { setProfileData({ ...profileData, targetingCategory: opt }); setContentTypeDropdownOpen(false); }}
                                  className={`w-full px-4 py-2.5 text-left flex items-center justify-between gap-3 transition-colors hover:bg-white/10 ${isSelected ? 'bg-emerald-500/15' : ''}`}
                                >
                                  <span className="text-gray-200 text-sm">{opt}</span>
                                  {isSelected && <CheckIcon className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <PricingHelper
                        followers={profileData.instagramFollowers}
                        onFollowersChange={(f) => setProfileData({ ...profileData, instagramFollowers: f })}
                        price={profileData.pricing}
                        onPriceChange={(p) => setProfileData({ ...profileData, pricing: p })}
                      />

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {loading ? 'Saving...' : 'Continue'}
                        <ArrowRightIcon className="w-4 h-4" />
                      </button>
                    </form>
                  </div>
                )}

                {/* Step 3: Promo Video */}
                {currentStep === 3 && (
                  <div className="p-6 sm:p-8 lg:p-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Your Promo Video</h2>
                    <p className="text-gray-600 mb-6">Introduce yourself in 30-60 seconds to fans who want a Personalized Video ShoutOut.</p>

                    <form onSubmit={handleVideoSubmit} className="space-y-5">
                      <details className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                        <summary className="font-medium text-blue-800 cursor-pointer">📝 Script Template</summary>
                        <div className="mt-3 text-sm text-blue-700 space-y-1">
                          <p><strong>1.</strong> "Hi! I'm [Your Name]..."</p>
                          <p><strong>2.</strong> "[Who you are - your background]"</p>
                          <p><strong>3.</strong> "I'm on ShoutOut - order a personalized video..."</p>
                          <p><strong>4.</strong> "[What makes your videos special]"</p>
                          <p><strong>5.</strong> "Find me on ShoutOut!"</p>
                        </div>
                      </details>

                      <div>
                        <input
                          type="file"
                          id="video-upload"
                          accept="video/*"
                          onChange={(e) => setPromoVideo(e.target.files?.[0] || null)}
                          className="hidden"
                        />
                        <label
                          htmlFor="video-upload"
                          className="flex items-center justify-center gap-3 p-6 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 transition-all"
                        >
                          <VideoCameraIcon className="w-8 h-8 text-gray-400" />
                          <span className="text-gray-600">{promoVideo ? promoVideo.name : 'Choose Video File'}</span>
                        </label>
                        <p className="text-xs text-gray-500 mt-2 text-center">MP4, MOV, AVI • Max 300MB</p>
                      </div>

                      {promoVideo && (
                        <div className="rounded-xl overflow-hidden bg-black">
                          <video src={URL.createObjectURL(promoVideo)} controls className="w-full" style={{ maxHeight: '300px' }} />
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={!promoVideo || videoUploading}
                        className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {videoUploading ? 'Uploading...' : 'Continue'}
                        <ArrowRightIcon className="w-4 h-4" />
                      </button>

                      <button
                        type="button"
                        onClick={() => setCurrentStep(4)}
                        className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        Skip for now — you can add this later
                      </button>
                    </form>
                  </div>
                )}

                {/* Step 4 (onboarding v2): put the link in their bio, wherever their fans are.
                    An exclusive link is suggested, never required (Nicky, 6 Oct): it only
                    matters for sponsorship campaigns, which say so in each offer. */}
                {currentStep === 4 && onboardingV2 && (
                  <div className="p-6 sm:p-8 lg:p-10 overflow-hidden rounded-2xl" style={{ background: '#ffffff' }}>
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <GlobeAltIcon className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="mb-2" style={{ fontSize: '1.5rem', lineHeight: '2rem', fontWeight: 700, color: '#111827' }}>Your Link In Bio Is Ready!</h2>
                      <p style={{ color: '#4b5563' }}>Now put it in your bio, wherever your fans find you.</p>
                    </div>

                    <div className="flex items-center justify-between gap-3 p-3 rounded-xl mb-4" style={{ background: '#ecfdf5', border: '1px solid #d1fae5' }}>
                      <span className="min-w-0 truncate font-semibold" style={{ color: '#047857' }}>
                        shoutout.fans/{profileData.username || 'yourname'}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const url = `https://shoutout.fans/${profileData.username}`;
                          void navigator.clipboard
                            ?.writeText(url)
                            .then(() => {
                              setBioLinkCopied(true);
                              setTimeout(() => setBioLinkCopied(false), 2500);
                            })
                            .catch(() => undefined);
                        }}
                        disabled={!profileData.username}
                        className="shrink-0 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                        style={{ background: '#059669', color: '#ffffff' }}
                      >
                        {bioLinkCopied ? 'Copied' : 'Copy my link'}
                      </button>
                    </div>

                    <ol className="list-decimal pl-5 space-y-1.5 mb-4 text-sm" style={{ color: '#374151' }}>
                      <li>Open Instagram, TikTok, YouTube, or wherever your fans are, and edit your profile.</li>
                      <li>Paste your ShoutOut link into the link field (Links or Website) and save, so fans can tap it.</li>
                    </ol>

                    <p className="p-3 rounded-xl mb-6 text-sm" style={{ background: '#f9fafb', border: '1px solid #e5e7eb', color: '#4b5563' }}>
                      Best results: make it the only link in your bio. Every tap lands right on your page instead of a list, which gives you the best chance to earn. You can move your other links onto your ShoutOut page as buttons.
                    </p>

                    <CreatorTermsCheckbox checked={termsAgreed} onChange={setTermsAgreed} />
                    <button
                      onClick={() => handleBioLinkComplete({ confirmBioLink: true })}
                      disabled={loading || !termsAgreed}
                      className="w-full py-4 bg-gradient-to-r from-emerald-500 to-cyan-500 font-bold text-lg rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25"
                      style={{ color: '#ffffff' }}
                    >
                      {loading ? 'Setting up your dashboard...' : 'I’ve added it'}
                      <ArrowRightIcon className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBioLinkComplete()}
                      disabled={loading || !termsAgreed}
                      className="w-full mt-2 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
                    >
                      I’ll do it later
                    </button>
                  </div>
                )}

                {/* Step 4: Your Bio Link */}
                {currentStep === 4 && !onboardingV2 && (
                  <div className="p-6 sm:p-8 lg:p-10 overflow-hidden rounded-2xl" style={{ background: '#ffffff' }}>
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <GlobeAltIcon className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="mb-2" style={{ fontSize: '1.5rem', lineHeight: '2rem', fontWeight: 700, color: '#111827' }}>Your Link In Bio Is Ready!</h2>
                      <p style={{ color: '#4b5563' }}>
                        You now have a <span className="font-semibold" style={{ color: '#059669' }}>shoutout.fans/{profileData.username || 'yourname'}</span> page.
                        Customize it from your dashboard — add links, social accounts, and more.
                      </p>
                    </div>

                    <div className="relative overflow-hidden mb-6 rounded-xl">
                      <div className="overflow-x-clip overflow-y-visible pb-4">
                        <div
                          ref={bioLinkCarouselRef}
                          className="flex gap-3"
                          style={{ width: 'max-content' }}
                        >
                          {[...bioCreatorBios, ...bioCreatorBios, ...bioCreatorBios].map((creator, index) => (
                            <div key={index} className="flex-shrink-0 w-[120px]">
                              <div className="relative rounded-xl overflow-hidden bg-[#1a1a2e]" style={{ boxShadow: '0 15px 30px -8px rgba(0, 0, 0, 0.15)' }}>
                                <div className="relative w-full h-[220px] overflow-hidden">
                                  <iframe
                                    src={`https://shoutout.fans/${creator.handle}?preview=true`}
                                    title={`${creator.name}'s bio`}
                                    className="border-0 rounded-xl origin-top-left pointer-events-none"
                                    loading="eager"
                                    scrolling="no"
                                    style={{
                                      width: '375px',
                                      height: '667px',
                                      transform: 'scale(0.32)',
                                      transformOrigin: 'top left',
                                    }}
                                  />
                                  <div className="absolute inset-0 cursor-default" />
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/80 to-transparent">
                                  <p className="font-medium text-[10px] text-white text-center">{creator.name}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-start gap-3 p-3 rounded-xl" style={{ background: '#ecfdf5', border: '1px solid #d1fae5' }}>
                        <CheckIcon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#047857' }} />
                        <p className="text-sm font-medium" style={{ color: '#1f2937' }}>Add your social links, YouTube, Rumble, podcast, and more</p>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-xl" style={{ background: '#ecfdf5', border: '1px solid #d1fae5' }}>
                        <CheckIcon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#047857' }} />
                        <p className="text-sm font-medium" style={{ color: '#1f2937' }}>Your ShoutOut order button is automatically included</p>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-xl" style={{ background: '#ecfdf5', border: '1px solid #d1fae5' }}>
                        <CheckIcon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#047857' }} />
                        <p className="text-sm font-medium" style={{ color: '#1f2937' }}>Add a sponsor card and earn $60–$120 per 100 clicks</p>
                      </div>
                    </div>

                    <CreatorTermsCheckbox checked={termsAgreed} onChange={setTermsAgreed} />
                    <button
                      onClick={() => handleBioLinkComplete()}
                      disabled={loading || !termsAgreed}
                      className="w-full py-4 bg-gradient-to-r from-emerald-500 to-cyan-500 font-bold text-lg rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25"
                      style={{ color: '#ffffff' }}
                    >
                      {loading ? 'Setting up your dashboard...' : 'Launch Your Creator Dashboard'}
                      <ArrowRightIcon className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* "Is this your page?": an unclaimed page has exactly this name or username */}
      {duplicatePrompt && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDuplicatePrompt(null)} />
          {/* Never taller than the screen: the pages scroll, the way out stays visible. */}
          <div className="relative w-full max-w-sm max-h-[calc(100dvh-2rem)] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="flex-shrink-0 bg-gradient-to-r from-emerald-500 to-cyan-500 px-6 py-4">
              <h3 className="text-white font-bold text-lg">Is this your page?</h3>
              <p className="text-emerald-50 text-xs mt-0.5">
                {duplicatePrompt.unclaimed.length === 1
                  ? 'A page with this name is already set up on ShoutOut.'
                  : 'Pages with this name are already set up on ShoutOut.'}
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-6 pb-3 space-y-3">
              {duplicatePrompt.unclaimed.map((page) => (
                <div key={page.id} className="flex items-center gap-3 rounded-xl border border-gray-200 p-3">
                  {page.avatar_url ? (
                    <img src={page.avatar_url} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gray-200 flex-shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900 truncate">{page.name}</p>
                    {page.username && <p className="text-xs text-gray-500 truncate">shoutout.fans/{page.username}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDuplicateClaim(page)}
                    className="flex-shrink-0 px-3 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-all"
                  >
                    Claim it
                  </button>
                </div>
              ))}
            </div>
            <div className="flex-shrink-0 border-t border-gray-100 px-6 py-3">
              <button
                type="button"
                onClick={() =>
                  void continueWithNewPage({ fullName: duplicatePrompt.fullName, username: duplicatePrompt.username }, true)
                }
                className="w-full py-2.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Not mine, set up a new page
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Claim-your-page confirmation modal */}
      {claimCandidate && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => !claiming && claimLinkStep !== 'sending' && closeClaimModal()}
          />
          <div className="relative w-full max-w-sm max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="bg-gradient-to-r from-emerald-500 to-cyan-500 px-6 py-4">
              <h3 className="text-white font-bold text-lg">Claim My Page</h3>
              <p className="text-emerald-50 text-xs mt-0.5">This page was set up for you — claim it to take it over.</p>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                {claimCandidate.avatar_url ? (
                  <img src={claimCandidate.avatar_url} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-gray-200 flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="font-bold text-gray-900 truncate">{claimCandidate.name}</p>
                  {claimCandidate.username && (
                    <p className="text-sm text-gray-500 truncate">shoutout.fans/{claimCandidate.username}</p>
                  )}
                </div>
              </div>
              {claimCandidate.bio && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-3">{claimCandidate.bio}</p>
              )}
              {claimLinkStep === 'sent' && claimFromPrompt ? (
                <>
                  <p className="text-sm text-gray-700 mb-4">
                    Request sent. When your claim link arrives, open it to take over this page. You don&apos;t need to
                    set up a new page.
                  </p>
                  <button
                    type="button"
                    onClick={closeClaimModal}
                    className="w-full py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all"
                  >
                    Got it
                  </button>
                  <button
                    type="button"
                    onClick={() => void continueWithNewPage(claimFromPrompt, false)}
                    className="w-full py-2.5 mt-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Set up a new page anyway
                  </button>
                </>
              ) : claimLinkStep && claimFromPrompt ? (
                <>
                  <p className="text-sm text-gray-700 mb-4">
                    A page is only handed over with its own claim link. We&apos;ll check it&apos;s you and send the
                    link, usually within one business day.
                  </p>
                  <button
                    type="button"
                    onClick={handleRequestClaimLink}
                    disabled={claimLinkStep === 'sending'}
                    className="w-full py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50"
                  >
                    {claimLinkStep === 'sending' ? 'Sending...' : 'Send me the claim link'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void continueWithNewPage(claimFromPrompt, true)}
                    disabled={claimLinkStep === 'sending'}
                    className="w-full py-2.5 mt-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    It&apos;s not mine, set up a new page
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleClaimProfile}
                    disabled={claiming}
                    className="w-full py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50"
                  >
                    {claiming ? 'Claiming...' : 'This is me.'}
                  </button>
                  <button
                    type="button"
                    onClick={closeClaimModal}
                    disabled={claiming}
                    className="w-full py-2.5 mt-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Not me
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* FOMO signup ticker (bottom-right) */}
      {!fomoDismissed && fomoCurrent && (
        <div
          className={`fixed bottom-4 right-4 z-[90] w-[300px] max-w-[calc(100vw-2rem)] transition-all duration-500 ease-out ${
            fomoVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
        >
          <div className="relative rounded-2xl bg-slate-800/95 backdrop-blur-md border border-white/10 shadow-2xl shadow-black/40 p-3.5 pr-9">
            <button
              type="button"
              onClick={() => setFomoDismissed(true)}
              aria-label="Dismiss"
              className="absolute top-2 right-2 p-1 rounded-full text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl overflow-hidden bg-gradient-to-br from-emerald-400 to-cyan-400 p-[2px] flex-shrink-0">
                <img src={fomoCurrent.avatar} alt={fomoCurrent.name} className="w-full h-full object-cover rounded-[10px]" />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-white leading-snug">
                  <span className="font-semibold">{fomoCurrent.name}</span>
                  <span className="text-gray-300"> just started using ShoutOut Fans</span>
                </p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <CheckBadgeIcon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span className="text-[11px] text-gray-400">{formatSignupDate(fomoCurrent.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StartPage;
